import { useLayoutEffect, useMemo, useState, type RefObject } from 'react'

import type { ExceptionColumnDef } from '../../../config/exceptions/types'
import {
  FREEZE_SHADOW,
  GROUP_SEPARATOR_BORDER,
} from '../../../theme/exceptionTableStyles'

/**
 * Centralised sticky offsets + z-index ladder for DataViewTable.
 *
 * Chrome heights here mirror `InspectorLayout`:
 *   - app header (`InspectorLayout.tsx`): `position: sticky; top: 0`, 64px
 *   - nav bar    (`InspectorLayout.tsx`): `position: sticky; top: 64px`, 56px
 *
 * Both expanded and collapsed states are surfaced through a single CSS
 * variable, `--app-chrome-height`, that `InspectorLayout` writes onto
 * the document root. Sticky `top:` values reference that variable via
 * `CHROME_OFFSET_CSS` so the table layout reacts to chrome collapse
 * without React re-renders.
 */

/** Expanded chrome: full app header (64) + nav (56). */
export const APP_HEADER_HEIGHT = 64
export const APP_NAV_HEIGHT = 56
export const CHROME_OFFSET = APP_HEADER_HEIGHT + APP_NAV_HEIGHT

/**
 * Collapsed chrome height (nav only). Used by the chrome-collapse-on-
 * scroll behaviour in `InspectorLayout`.
 */
export const CHROME_OFFSET_COLLAPSED = APP_NAV_HEIGHT

/**
 * CSS expression for the current chrome offset. Falls back to the
 * expanded numeric value if `--app-chrome-height` has not been set
 * (e.g. when DataViewTable is rendered outside InspectorLayout).
 */
export const CHROME_OFFSET_CSS = `var(--app-chrome-height, ${CHROME_OFFSET}px)`

/** Read the current chrome offset in pixels (for JS-side scroll math). */
export function readChromeOffsetPx(): number {
  if (typeof window === 'undefined') return CHROME_OFFSET
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue('--app-chrome-height')
    .trim()
  if (!raw) return CHROME_OFFSET
  const parsed = parseFloat(raw)
  return Number.isFinite(parsed) ? parsed : CHROME_OFFSET
}

/**
 * Sticky-stack z-index map.
 *   50  AppBar (InspectorLayout teal header)             [layout-owned]
 *   40  Nav bar (InspectorLayout nav)                    [layout-owned]
 *    6  Results toolbar (sticky to outer scroll)
 *    5  Group header row / left-frozen header cell
 *    4  Column header row
 *    2  Frozen body cells (left-sticky)
 */
export const Z_TOOLBAR = 6
export const Z_GROUP_HEADER = 5
export const Z_COLUMN_HEADER = 4
export const Z_FROZEN_HEADER = 5
export const Z_FROZEN_BODY = 2

export interface StickyTopsInput {
  /**
   * When `true` the table keeps its own scroll container (`TableContainer`
   * with `overflow: auto`) - header cells pin to that container so their
   * `top:` is measured from its top edge and the toolbar/chrome above
   * contribute nothing.
   *
   * When `false` the table shares one scroll context with whatever the
   * outer scroller is (window for embedded inspector, DialogContent for
   * fullscreen). Toolbar + header cells all pin to that outer scroll.
   */
  useInnerScroll: boolean
  /**
   * CSS expression (string) or pixel number for the fixed chrome above
   * the outer scroll container. Pass `CHROME_OFFSET_CSS` for embedded
   * inspector (so the value tracks `--app-chrome-height` on collapse);
   * pass `0` for fullscreen Dialog and platform variant.
   */
  chromeOffset: number | string
  toolbarH: number
  groupHeaderH: number
  hasColumnGroups: boolean
}

export interface StickyTops {
  toolbar: string
  groupHeader: string
  columnHeader: string
}

const toCssLength = (value: number | string): string =>
  typeof value === 'number' ? `${value}px` : value

/**
 * Track a referenced element's rendered height via ResizeObserver. Used
 * by `DataViewTable` to flow the toolbar + group-header heights
 * into `computeStickyTops` without hard-coding either value. Returns 0
 * when the ref is unattached (e.g. group header row not rendered).
 *
 * Effect has no deps array on purpose: the underlying DOM node can swap
 * between renders (the group-header row only mounts when
 * `hasColumnGroups` is true), so we re-bind the observer each pass.
 */
export function useMeasuredHeight(ref: RefObject<HTMLElement | null>): number {
  const [height, setHeight] = useState(0)
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) {
      setHeight(0)
      return
    }
    const measure = () => setHeight(node.getBoundingClientRect().height)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  })
  return height
}

// ---------------------------------------------------------------------------
// Frozen-column metadata (left + right edges)
// ---------------------------------------------------------------------------
//
// `freeze: 'left' | 'right'` columns are pinned via `position: sticky` plus
// a cumulative offset off the relevant edge. The maps below carry the
// per-column offset, width, and whether the column is the "boundary" cell
// (last frozen on the left / first frozen on the right) so the freeze-edge
// drop-shadow can be applied to only that column.
//
// Both maps are computed from the same source-order array (`visibleColumns`).
// Right-frozen columns must be contiguous at the END of that array; the
// rightmost column gets `right: 0`, then each earlier right-frozen column
// accumulates the widths to its right.

export interface FreezeLeftMeta {
  left: number
  width: number
  isLastFrozen: boolean
}

export interface FreezeRightMeta {
  right: number
  width: number
  isFirstFrozenRight: boolean
}

const FREEZE_SHADOW_RIGHT = '-2px 0 4px rgba(0,0,0,0.05)'

export function buildLeftFreezeMeta(
  visibleColumns: ExceptionColumnDef[],
): Map<string, FreezeLeftMeta> {
  const result = new Map<string, FreezeLeftMeta>()
  let cumulative = 0
  const frozenCols = visibleColumns.filter((c) => c.freeze === 'left')
  frozenCols.forEach((col, idx) => {
    // Prefer explicit width — fixes the "column slides under the next
    // one" bug that happens when minWidth differs from actual render.
    const width = col.width ?? col.minWidth ?? 120
    result.set(col.key, {
      left: cumulative,
      width,
      isLastFrozen: idx === frozenCols.length - 1,
    })
    cumulative += width
  })
  return result
}

export function buildRightFreezeMeta(
  visibleColumns: ExceptionColumnDef[],
): Map<string, FreezeRightMeta> {
  const result = new Map<string, FreezeRightMeta>()
  const rightFrozenCols = visibleColumns.filter((c) => c.freeze === 'right')
  // Walk in reverse so the rightmost column gets right:0, then each
  // earlier column accumulates the widths to its right.
  let cumulative = 0
  for (let i = rightFrozenCols.length - 1; i >= 0; i--) {
    const col = rightFrozenCols[i]
    const width = col.width ?? col.minWidth ?? 120
    result.set(col.key, {
      right: cumulative,
      width,
      isFirstFrozenRight: i === 0,
    })
    cumulative += width
  }
  return result
}

/**
 * Compose the full sx object for a frozen cell. Reads from the left-
 * and right-freeze maps; if the column isn't in either it returns an
 * empty object so the caller can spread unconditionally. Used by both
 * the header (cells with `backgroundColor: <header-bg>`, header
 * z-index) and the body (cells with `backgroundColor: 'var(--row-bg)'`,
 * body z-index).
 */
export function getFreezeSx(
  col: ExceptionColumnDef,
  freezeMeta: Map<string, FreezeLeftMeta>,
  freezeMetaRight: Map<string, FreezeRightMeta>,
  { backgroundColor, zIndex }: { backgroundColor: string; zIndex: number },
): Record<string, any> {
  const meta = freezeMeta.get(col.key)
  if (meta) {
    // The auto-injected bulk-select column is too narrow (44 px) to
    // wear the standard freeze drop-shadow without the checkbox
    // visually sitting on top of the line. When it's the only
    // frozen-left column the boundary still needs *something* so the
    // sticky cell reads as separated - use the subtle 1px group
    // separator border instead. On configs with more frozen columns
    // (e.g. sales-compare's sale_date/client/invoice_number), the
    // shadow still lands on the last of those and the bulk-tick keeps
    // the regular inter-cell border between two sticky cells.
    const isSelectColumn = col.key === '__select__'
    const edgeSx = meta.isLastFrozen
      ? isSelectColumn
        ? { borderRight: GROUP_SEPARATOR_BORDER }
        : { boxShadow: FREEZE_SHADOW }
      : {}
    return {
      position: 'sticky',
      left: meta.left,
      width: meta.width,
      minWidth: meta.width,
      maxWidth: meta.width,
      zIndex,
      backgroundColor,
      ...edgeSx,
    }
  }
  const metaR = freezeMetaRight.get(col.key)
  if (!metaR) return {}
  return {
    position: 'sticky',
    right: metaR.right,
    width: metaR.width,
    minWidth: metaR.width,
    maxWidth: metaR.width,
    zIndex,
    backgroundColor,
    ...(metaR.isFirstFrozenRight ? { boxShadow: FREEZE_SHADOW_RIGHT } : {}),
  }
}

/**
 * Memoised left + right freeze maps for a given `visibleColumns` array.
 * The host component calls this in render and passes the results to
 * `getFreezeSx` from the header / body row builders.
 */
export function useFreezeMeta(visibleColumns: ExceptionColumnDef[]): {
  freezeMeta: Map<string, FreezeLeftMeta>
  freezeMetaRight: Map<string, FreezeRightMeta>
} {
  const freezeMeta = useMemo(
    () => buildLeftFreezeMeta(visibleColumns),
    [visibleColumns],
  )
  const freezeMetaRight = useMemo(
    () => buildRightFreezeMeta(visibleColumns),
    [visibleColumns],
  )
  return { freezeMeta, freezeMetaRight }
}

/**
 * Compute the sticky `top:` values for the three pinned bands as CSS
 * length expressions. Outputs strings so the embedded-inspector path can
 * weave a CSS variable into the values; consumers spread them straight
 * into `sx={{ top: ... }}`.
 */
export function computeStickyTops({
  useInnerScroll,
  chromeOffset,
  toolbarH,
  groupHeaderH,
  hasColumnGroups,
}: StickyTopsInput): StickyTops {
  if (useInnerScroll) {
    return {
      toolbar: toCssLength(chromeOffset),
      groupHeader: '0px',
      columnHeader: hasColumnGroups ? `${groupHeaderH}px` : '0px',
    }
  }
  const base = toCssLength(chromeOffset)
  const groupOffset = toolbarH
  const columnOffset = toolbarH + (hasColumnGroups ? groupHeaderH : 0)
  return {
    toolbar: base,
    groupHeader: `calc(${base} + ${groupOffset}px)`,
    columnHeader: `calc(${base} + ${columnOffset}px)`,
  }
}
